import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { analyticsService, adminService } from '../../services';
import { useUser } from '../../context/UserContext';
import { AdminModal, AdminPageHeader, Card, Chip, DataTable, DrillList, EmptyLine, SegTrack, StatTile, TileGrid, relTime, Loading, useAdminShell } from '../../components/admin/shell';
import { LineChart, ColumnChart, BarList, Avatar, Sparkline, fmt, hms } from '../../components/admin/analytics/charts';
import ActivityMap from '../../components/admin/analytics/ActivityMap';

const initialsOf = (name) => (name || '?').split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase();

/**
 * OVERVIEW › ANALYTICS. One filter row scopes everything below it: the range, whether
 * staff and test accounts count, and any person excluded by name. ONLINE NOW polls every
 * 30 s. Every chart carries an axis and a tooltip; the map is a real one.
 */
export default function AnalyticsPage() {
  const { can } = useUser();
  const { openPerson, canSeePeople } = useAdminShell();
  const [dayDrill, setDayDrill] = useState(null);
  const [params, setParams] = useSearchParams();
  const days = Number(params.get('days') || 30);
  const excludeStaff = params.get('staff') !== 'in';
  const excludeTest = params.get('test') !== 'in';
  const excluded = useMemo(() => (params.get('ex') || '').split(',').filter(Boolean).map((s) => { const [id, ...n] = s.split('~'); return { id, name: n.join('~') || 'USER' }; }), [params]);
  const filter = useMemo(() => ({ days, excludeStaff, excludeTest, excludeUserIds: excluded.map((e) => e.id), take: 25 }), [days, excludeStaff, excludeTest, excluded]);

  const set = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => { if (v === undefined || v === null || v === '') next.delete(k); else next.set(k, String(v)); });
    setParams(next, { replace: true });
  };
  const exclude = (id, name) => { if (!excluded.some((e) => e.id === id)) set({ ex: [...excluded, { id, name }].map((e) => `${e.id}~${e.name}`).join(',') }); };
  const include = (id) => set({ ex: excluded.filter((e) => e.id !== id).map((e) => `${e.id}~${e.name}`).join(',') });

  const [data, setData] = useState(null);
  const [people, setPeople] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    const [o, p] = await Promise.all([analyticsService.overview(filter), analyticsService.people(filter)]);
    setLoading(false);
    if (o.success) setData(o.data);
    if (p.success) setPeople(p.data);
  }, [filter]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(async () => { const p = await analyticsService.people(filter); if (p.success) setPeople(p.data); }, 30_000);
    return () => clearInterval(t);
  }, [filter]);

  const t = data?.totals || {};
  const online = people?.onlineNow || [];
  const top = people?.topUsers || [];
  const canOpenUsers = can('users', 'view');
  const asBreakdown = (items = []) => items.map((i) => ({ label: i.label, value: i.count, display: fmt(i.count) }));
  const secBreakdown = (items = []) => items.map((i) => ({ label: i.label, value: i.count, display: hms(i.count) }));

  /** A day on the traffic chart, drilled: who was on the site, where from, what they read. */
  const openDay = async (date) => {
    setDayDrill({ date, loading: true });
    const r = await analyticsService.day(date, filter);
    setDayDrill(r.success ? { date, data: r.data } : { date, error: r.error || 'Could not load that day.' });
  };

  return (
    <>
      <AdminPageHeader section="analytics" meta="Visitors, sessions, pages and the Portal → application → checkout funnel. Filters scope everything below them." />
      <div className={`flex flex-col gap-5 transition-opacity ${loading && data ? 'opacity-60' : ''}`}>
        {/* Filters — one row, range first. */}
        <div className="flex flex-wrap items-center gap-2">
          <SegTrack ariaLabel="Range" value={days} onChange={(d) => set({ days: d })} options={[7, 30, 90].map((d) => ({ value: d, label: `${d}D` }))} />
          <Chip selected={excludeStaff} onClick={() => set({ staff: excludeStaff ? 'in' : '' })}>EXCLUDE STAFF</Chip>
          <Chip selected={excludeTest} onClick={() => set({ test: excludeTest ? 'in' : '' })}>EXCLUDE TEST ACCOUNTS</Chip>
          {canOpenUsers && <PersonPicker onPick={(u) => exclude(u.id, u.displayName || u.email)} />}
          {excluded.map((e) => (
            <button key={e.id} type="button" onClick={() => include(e.id)} className="h-[38px] rounded-full bg-black px-4 font-mono text-[11px] font-bold uppercase tracking-[0.12em] text-white hover:opacity-85" title="Include again">
              {e.name} ✕
            </button>
          ))}
          {data && data.excludedPeople > 0 && <span className="font-mono text-[11px] text-[#6B7280]">{data.excludedPeople} {data.excludedPeople === 1 ? 'person' : 'people'} filtered out of this window.</span>}
        </div>

        {/* ONLINE NOW */}
        <Card title={`ONLINE NOW · ${online.length}`} right="LAST 5 MINUTES · REFRESHES EVERY 30 S">
          {!people ? <Loading /> : online.length === 0 ? (
            <div className="font-mono text-[11px] font-bold uppercase tracking-[0.1em] text-[#6B7280]">NOBODY ON THE SITE RIGHT NOW.</div>
          ) : (
            <div className="flex flex-wrap gap-3">
              {online.map((o) => (
                <button key={o.userId} type="button" onClick={() => canSeePeople && openPerson(o.userId)} className="flex items-center gap-[10px] rounded-full bg-[#F7F7F7] py-2 pl-2 pr-4 text-left transition-colors duration-200 hover:bg-[#F2F2F2]" title={`${o.name} · last seen ${relTime(o.lastSeen)}`}>
                  <Avatar url={o.avatarUrl} initials={initialsOf(o.name)} size={32} />
                  <span className="min-w-0">
                    <span className="block truncate font-mono text-[12px] font-bold">{o.name}{o.isStaff ? <span className="ml-2 text-[9px] font-bold tracking-[0.1em] text-[#6B7280]">STAFF</span> : null}{o.isTest ? <span className="ml-2 text-[9px] font-bold tracking-[0.1em] text-[#6B7280]">TEST</span> : null}</span>
                    <span className="block truncate font-mono text-[10px] text-[#6B7280]">{o.path ? `on ${o.path}` : 'active'} · {hms(o.sessionSec)}{o.city ? ` · ${o.city}` : ''}{o.device ? ` · ${o.device}` : ''}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </Card>

        {!data ? (loading ? <Card><Loading /></Card> : <Card bodyClass="px-3 pb-3"><EmptyLine>NO ANALYTICS YET.</EmptyLine></Card>) : (
          <>
            <TileGrid min="190px">
              <StatTile label="UNIQUE VISITORS" value={fmt(t.uniqueVisitors)} />
              <StatTile label="SIGNED-IN USERS" value={fmt(t.signedInUsers)} />
              <StatTile label="SESSIONS" value={fmt(t.sessions)} />
              <StatTile label="SIGNED-IN SESSIONS" value={fmt(t.signedInSessions)} />
              <StatTile label="PAGE VIEWS" value={fmt(t.pageViews)} />
              <StatTile label="PAGES / SESSION" value={(t.pagesPerSession ?? 0).toFixed(1)} />
              <StatTile label="AVG SESSION" value={hms(t.avgEngagementSec)} />
              <StatTile label="TOTAL TIME" value={hms(t.totalEngagementSec)} />
            </TileGrid>

            <Card title={`TRAFFIC · ${data.days} DAYS`} right="HOVER A DAY FOR WHERE IT CAME FROM" bodyClass="px-5 pb-3">
              <LineChart
                label="Traffic over time"
                points={data.series}
                series={[{ key: 'pageViews', label: 'PAGE VIEWS' }, { key: 'visitors', label: 'VISITORS' }]}
                source="analytics_events GROUP BY day; the referrer is the tracker's first touch per session"
                breakdownFor={(i) => ({ breakdownLabel: 'HOW THEY ARRIVED', breakdown: asBreakdown(data.series[i]?.byReferrer) })}
                drill="SEE WHO VISITED →"
                onDrill={(i) => openDay(data.series[i].date)}
                axisInfo={{
                  title: 'TRAFFIC',
                  kicker: 'PER DAY',
                  desc: 'A page view is one route change. A visitor is one browser, counted once a day, signed in or not.',
                  breakdownLabel: 'HOW THEY ARRIVED',
                  breakdown: asBreakdown(data.topReferrers),
                  source: "analytics_events WHERE type = 'page_view' GROUP BY day",
                }}
              />
            </Card>

            <Card title={`TIME ON SITE · ${data.days} DAYS`} right="MINUTES · HOVER A DAY FOR WHERE IT WENT" bodyClass="px-3 pb-3">
              <ColumnChart
                height={170}
                format={(v) => `${fmt(v)}m`}
                items={data.series.map((p) => ({
                  key: p.date,
                  label: shortDayLabel(p.date),
                  value: Math.round((p.engagementSec || 0) / 60),
                  tip: [['ACTIVE TIME', hms(p.engagementSec)], ['SESSIONS', fmt(p.sessions)], ['PAGE VIEWS', fmt(p.pageViews)]],
                  breakdownLabel: 'WHERE IT WENT',
                  breakdown: secBreakdown(p.byArea),
                  source: "analytics_events WHERE type = 'engagement' GROUP BY day",
                }))}
                axisInfo={{
                  title: 'TIME ON SITE',
                  kicker: 'ACTIVE MINUTES',
                  desc: 'Active time only — the clock stops when the tab is hidden, and a single sample is capped at 30 minutes so a forgotten tab cannot skew it.',
                  breakdownLabel: 'WHERE THE TIME GOES',
                  breakdown: secBreakdown(data.timeByArea),
                  source: "analytics_events WHERE type = 'engagement' SUM(duration_ms)",
                }}
              />
            </Card>

            <div className="grid gap-[14px]" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
              <Card title="VISITS BY PERSON" right={`TOP ${top.length} OF ${fmt(people?.peopleInWindow)}`} bodyClass="px-3 pb-3">
                <ColumnChart items={top.map((p) => ({ key: p.userId, label: p.name, value: p.visits, avatarUrl: p.avatarUrl, initials: initialsOf(p.name), tip: [['VISITS', fmt(p.visits)], ['PAGE VIEWS', fmt(p.pageViews)], ['TIME', hms(p.timeSec)], ['LAST SEEN', relTime(p.lastSeen)]] }))} onSelect={(it) => canSeePeople && openPerson(it.key)} drill="OPEN THE PERSON →" />
              </Card>
              <Card title="TIME ON SITE BY PERSON" right="MINUTES" bodyClass="px-3 pb-3">
                <ColumnChart items={[...top].sort((a, b) => b.timeSec - a.timeSec).map((p) => ({ key: p.userId, label: p.name, value: Math.round(p.timeSec / 60), avatarUrl: p.avatarUrl, initials: initialsOf(p.name), tip: [['TIME', hms(p.timeSec)], ['VISITS', fmt(p.visits)], ['PAGE VIEWS', fmt(p.pageViews)], ['LAST SEEN', relTime(p.lastSeen)]] }))} format={(v) => `${fmt(v)}m`} onSelect={(it) => canSeePeople && openPerson(it.key)} drill="OPEN THE PERSON →" />
              </Card>
            </div>

            <DataTable
              title="TOP USERS"
              columns={[
                { key: 'name', label: 'PERSON', fr: 2, render: (p) => <span className="flex items-center gap-2"><Avatar url={p.avatarUrl} initials={initialsOf(p.name)} size={24} /><span className="truncate">{p.name}{p.handle ? <span className="ml-2 font-normal text-[#6B7280]">@{p.handle}</span> : null}</span></span> },
                { key: 'visits', label: 'VISITS', fr: 0.7, right: true, render: (p) => fmt(p.visits) },
                { key: 'pageViews', label: 'PAGES', fr: 0.7, right: true, render: (p) => fmt(p.pageViews) },
                { key: 'timeSec', label: 'TIME', fr: 0.9, right: true, render: (p) => hms(p.timeSec) },
                { key: 'daily', label: 'BY DAY', fr: 1.1, render: (p) => <Sparkline values={p.dailyVisits || []} /> },
                { key: 'topPath', label: 'MOST VISITED', fr: 1.4, render: (p) => p.topPath || '—' },
                { key: 'city', label: 'CITY', fr: 1, render: (p) => p.city || '—' },
                { key: 'lastSeen', label: 'LAST SEEN', fr: 0.9, render: (p) => relTime(p.lastSeen) },
              ]}
              rows={top}
              rowKey={(p) => p.userId}
              onRowClick={canSeePeople ? (p) => openPerson(p.userId) : undefined}
              hasActions
              actions={(p) => [{ label: 'EXCLUDE', onClick: () => exclude(p.userId, p.name) }]}
              empty="NO ONE IN THIS RANGE."
              footerLeft={`${fmt(people?.peopleInWindow)} people in the window`}
            />

            <Card title="ACTIVITY MAP" right="BY CITY · CIRCLE = ACTIVITY" bodyClass="px-3 pb-3">
              <ActivityMap cities={data.topCities} />
            </Card>

            <div className="grid gap-[14px]" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
              <Card title="HOW THEY ARRIVED" right="SESSIONS" bodyClass="px-3 pb-3">
                <BarList items={(data.topReferrers || []).map((r) => ({ label: r.label, count: r.count }))} source="the tracker's first touch, per session" />
              </Card>
              <Card title="WHERE THE TIME GOES" right="ACTIVE TIME" bodyClass="px-3 pb-3">
                <BarList items={(data.timeByArea || []).map((a) => ({ label: a.label, count: a.count }))} format={(v) => hms(v)} source="analytics_events WHERE type = 'engagement' GROUP BY the area of the path" />
              </Card>
              <Card title="TOP PAGES" bodyClass="px-3 pb-3"><BarList items={data.topPages} /></Card>
              <Card title="TOP CITIES" bodyClass="px-3 pb-3"><BarList items={data.topCities.slice(0, 10).map((c) => ({ label: c.city, count: c.count }))} /></Card>
              <Card title="DEVICES" bodyClass="px-3 pb-3"><BarList items={data.devices.map((d) => ({ label: d.label.toUpperCase(), count: d.count }))} /></Card>
              <Card title="FUNNEL" bodyClass="px-3 pb-3">
                <BarList items={[{ label: 'PORTALS', count: data.funnel.signalsCreated }, { label: 'APPLICATIONS', count: data.funnel.applicationsSent }, { label: 'CHECKOUTS', count: data.funnel.checkoutsPaid }]} />
              </Card>
              <Card title="EVENTS BY TYPE" bodyClass="px-3 pb-3"><BarList items={data.topEvents.map((e) => ({ label: e.label.replace(/_/g, ' ').toUpperCase(), count: e.count }))} /></Card>
            </div>
          </>
        )}
      </div>

      <AdminModal
        open={!!dayDrill}
        onClose={() => setDayDrill(null)}
        title={dayDrill ? `ON THE SITE · ${shortDayLabel(dayDrill.date)}` : ''}
        kicker={dayDrill?.data
          ? `${fmt(dayDrill.data.visitors)} visitors · ${fmt(dayDrill.data.sessions)} sessions · ${fmt(dayDrill.data.pageViews)} page views · ${fmt(dayDrill.data.anonymousVisitors)} with no account.`
          : undefined}
        width={560}
      >
        {dayDrill?.loading && <Loading />}
        {dayDrill?.error && <div className="font-mono text-[12px] text-[#DC2626]">{dayDrill.error}</div>}
        {dayDrill?.data && (
          <div className="flex flex-col gap-4 pb-2">
            <DrillList
              items={(dayDrill.data.people || []).map((p) => ({
                key: p.userId,
                label: p.name,
                sub: [p.handle ? `@${p.handle}` : null, p.topPath, hms(p.timeSec)].filter(Boolean).join(' · '),
                display: `${fmt(p.pageViews)} PAGES`,
                image: p.avatarUrl || null,
                onClick: canSeePeople ? () => { setDayDrill(null); openPerson(p.userId); } : undefined,
              }))}
              empty="NOBODY SIGNED IN THAT DAY."
            />
            <div className="grid gap-[10px]" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
              <div className="rounded-[20px] bg-[#F7F7F7] px-3 py-3">
                <div className="px-2 pb-2 font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-[#9CA3AF]">HOW THEY ARRIVED</div>
                <BarList items={(dayDrill.data.referrers || []).map((r) => ({ label: r.label, count: r.count }))} />
              </div>
              <div className="rounded-[20px] bg-[#F7F7F7] px-3 py-3">
                <div className="px-2 pb-2 font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-[#9CA3AF]">WHAT THEY READ</div>
                <BarList items={(dayDrill.data.pages || []).map((r) => ({ label: r.label, count: r.count }))} />
              </div>
            </div>
          </div>
        )}
      </AdminModal>
    </>
  );
}

/** "OCT 14" — the one place a day is spelled on this page. */
function shortDayLabel(iso) {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return String(iso);
  return `${d.toLocaleString('en', { month: 'short' }).toUpperCase()} ${d.getDate()}`;
}

/** EXCLUDE PERSON… — a typeahead over accounts (users:view). */
function PersonPicker({ onPick }) {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  useEffect(() => {
    clearTimeout(timer.current);
    if (q.trim().length < 2) { setHits([]); return undefined; }
    timer.current = setTimeout(async () => {
      const r = await adminService.getUsers(1, q.trim());
      if (r.success) setHits((r.data.users || []).slice(0, 8));
    }, 250);
    return () => clearTimeout(timer.current);
  }, [q]);
  return (
    <div className="relative">
      <input
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="EXCLUDE PERSON…"
        aria-label="Exclude a person by name or email"
        className="h-[38px] min-w-[200px] rounded-full border-2 border-transparent bg-white px-4 font-mono text-[11px] font-bold uppercase tracking-[0.1em] placeholder:text-[#9CA3AF] focus:border-black focus:outline-none focus:ring-0"
      />
      {open && hits.length > 0 && (
        <div className="absolute left-0 top-[calc(100%+8px)] z-30 min-w-[280px] overflow-hidden rounded-[22px] bg-[#0A0A0A] p-2">
          {hits.map((u) => (
            <button key={u.id} type="button" onMouseDown={() => { onPick(u); setQ(''); setHits([]); }} className="flex w-full flex-col gap-[2px] rounded-[14px] px-[14px] py-[10px] text-left font-mono text-white hover:bg-[#1F1F1F]">
              <span className="text-[11px] font-bold">{u.displayName || u.email}</span>
              <span className="text-[10px] text-[#8A8A8A]">{u.email}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
